import { z } from 'zod';
import { TIME_ENTRY_SOURCES, TIME_ENTRY_STATUSES } from '../constants/time-clock.js';
import { moneyCentsSchema, paginatedSchema, patchableSchema, uuidSchema } from './common.js';
/** A calendar day in the store's timezone. */
const storeDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');
const noteSchema = z.string().trim().max(500);
export const timeEntrySchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    clockInAt: z.iso.datetime(),
    clockOutAt: z.iso.datetime().nullable(),
    note: z.string().nullable(),
    source: z.enum(TIME_ENTRY_SOURCES),
    status: z.enum(TIME_ENTRY_STATUSES),
    /** Null while the entry is open. */
    durationMs: z.number().int().nonnegative().nullable(),
    editedAt: z.iso.datetime().nullable(),
    editedByName: z.string().nullable(),
    voidedAt: z.iso.datetime().nullable(),
    voidedByName: z.string().nullable(),
    voidReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const timeEntryListItemSchema = timeEntrySchema.extend({ userName: z.string() });
export const punchSchema = z.object({ note: noteSchema.optional() });
export const timeClockStatusSchema = z.object({ openEntry: timeEntrySchema.nullable() });
export const timeEntryListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    userId: uuidSchema.optional(),
    /** Inclusive store-timezone days; an entry counts if it overlaps the window. */
    from: storeDateSchema.optional(),
    to: storeDateSchema.optional(),
    /** `active` is open + closed (everything that is not voided). */
    status: z.enum(['active', ...TIME_ENTRY_STATUSES, 'all']).default('active'),
});
export const timeEntryListResponseSchema = paginatedSchema(timeEntryListItemSchema);
export const createTimeEntrySchema = z.object({
    userId: uuidSchema,
    clockInAt: z.iso.datetime(),
    clockOutAt: z.iso.datetime().optional(),
    note: noteSchema.optional(),
});
export const updateTimeEntrySchema = patchableSchema(z.object({
    clockInAt: z.iso.datetime(),
    clockOutAt: z.iso.datetime(),
    note: noteSchema.nullable(),
}));
export const voidTimeEntrySchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const timeClockSummaryQuerySchema = z.object({
    from: storeDateSchema,
    to: storeDateSchema,
    userId: uuidSchema.optional(),
});
export const timeClockSummarySchema = z.object({
    from: storeDateSchema,
    to: storeDateSchema,
    rows: z.array(z.object({
        userId: uuidSchema,
        userName: z.string(),
        entryCount: z.number().int().nonnegative(),
        totalMs: z.number().int().nonnegative(),
        hasOpenEntry: z.boolean(),
    })),
    totalMs: z.number().int().nonnegative(),
});
/** One hourly rate and the day it starts applying (t1188). */
export const staffPayRateSchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    rateCents: z.number().int().positive(),
    effectiveFrom: storeDateSchema,
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const setStaffPayRateSchema = z.object({
    userId: uuidSchema,
    rateCents: moneyCentsSchema.refine((cents) => cents > 0, 'Enter a rate above zero'),
    effectiveFrom: storeDateSchema,
});
export const staffPayRatesResponseSchema = z.object({
    rows: z.array(z.object({
        userId: uuidSchema,
        userName: z.string(),
        /** The rate in force today, or null if none has started yet. */
        currentRateCents: z.number().int().positive().nullable(),
        /** Every rate on record, newest first. */
        history: z.array(staffPayRateSchema),
    })),
});
export const timeClockPayQuerySchema = timeClockSummaryQuerySchema;
export const timeClockPaySchema = z.object({
    from: storeDateSchema,
    to: storeDateSchema,
    rows: z.array(z.object({
        userId: uuidSchema,
        userName: z.string(),
        totalMs: z.number().int().nonnegative(),
        /** Closed time that had a rate to price it at. */
        ratedMs: z.number().int().nonnegative(),
        /** Closed time worked before the person's first rate; not priced. */
        unratedMs: z.number().int().nonnegative(),
        payCents: z.number().int().nonnegative(),
        /** Distinct hourly rates used in the period, lowest first. */
        rates: z.array(z.number().int().positive()),
        hasOpenEntry: z.boolean(),
    })),
    totalMs: z.number().int().nonnegative(),
    totalPayCents: z.number().int().nonnegative(),
});
//# sourceMappingURL=time-clock.js.map